'use client'

import clsx from 'clsx'
import React, { useState } from 'react'

interface SizeCardProps {
  title: string
  maxWeight: string
  maxDimensions: string
  individualDimensions: {
    length: number
    width: number
    height: number
  }
  isExpanded?: boolean
  onToggle?: () => void
}

const SizeCard: React.FC<SizeCardProps> = ({
  title,
  maxWeight,
  maxDimensions,
  individualDimensions,
  isExpanded = true,
  onToggle,
}) => {
  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-lg flex-1">
      {/* Mobile collapsible header */}
      <div
        className="lg:hidden p-4 flex items-center justify-between cursor-pointer"
        onClick={onToggle}
      >
        <h3 className="text-lg font-bold text-gray-900">{title}</h3>
        <svg
          className={clsx(
            'w-5 h-5 text-gray-500 transition-transform',
            isExpanded ? 'rotate-180' : '',
          )}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </div>

      {/* Content - always visible on desktop, collapsible on mobile */}
      <div
        className={clsx(
          'p-8 text-center',
          'lg:block',
          isExpanded ? 'block' : 'hidden',
        )}
      >
        <h3 className="text-2xl font-bold text-gray-900 mb-6 hidden lg:block">
          {title}
        </h3>

        <div className="mb-6">
          <p className="text-sm text-gray-500 mb-2">Max weight</p>
          <p className="text-2xl font-bold text-gray-900">{maxWeight}</p>
        </div>

        <div className="border-t border-gray-200 pt-6">
          <p className="text-sm text-gray-500 mb-3">Max dimensions (in)</p>
          <div className="flex flex-col items-center justify-center gap-4">
            <p className="text-lg font-bold text-gray-900">{maxDimensions}</p>
            <span className="text-sm text-gray-400">OR</span>
            <div className="flex items-center gap-2">
              <div className="text-center">
                <p className="text-lg font-bold text-gray-900">
                  {individualDimensions.length}
                </p>
                <p className="text-xs text-gray-500">L</p>
              </div>
              <span className="text-lg font-bold text-gray-900 mx-2">+</span>
              <div className="text-center">
                <p className="text-lg font-bold text-gray-900">
                  {individualDimensions.width}
                </p>
                <p className="text-xs text-gray-500">W</p>
              </div>
              <span className="text-lg font-bold text-gray-900 mx-2">+</span>
              <div className="text-center">
                <p className="text-lg font-bold text-gray-900">
                  {individualDimensions.height}
                </p>
                <p className="text-xs text-gray-500">H</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export interface WinterLuggageSizesProps {
  className?: string
}

const WinterLuggageSizes: React.FC<WinterLuggageSizesProps> = ({
  className,
}) => {
  const [expandedCards, setExpandedCards] = useState<{
    [key: string]: boolean
  }>({
    snowBoots: false,
    checked: false,
    oversized: false,
  })

  const toggleCard = (cardKey: string) => {
    setExpandedCards((prev) => ({
      ...prev,
      [cardKey]: !prev[cardKey],
    }))
  }

  return (
    <div className={clsx('bg-white py-12 px-6', className)}>
      <div className="max-w-6xl mx-auto">
        <h2 className="text-3xl font-bold text-gray-900 mb-8">
          Snow boots and Luggage
        </h2>

        <div className="flex flex-col lg:flex-row gap-6">
          <SizeCard
            title="Snow Boots"
            maxWeight="25 lbs"
            maxDimensions="55 in (L + W + H)"
            individualDimensions={{
              length: 20,
              width: 20,
              height: 15,
            }}
            isExpanded={expandedCards.snowBoots}
            onToggle={() => toggleCard('snowBoots')}
          />

          <SizeCard
            title="Checked"
            maxWeight="50 lbs"
            maxDimensions="62 in (L + W + H)"
            individualDimensions={{
              length: 26,
              width: 16,
              height: 20,
            }}
            isExpanded={expandedCards.checked}
            onToggle={() => toggleCard('checked')}
          />
          <SizeCard
            title="Oversized"
            maxWeight="65 lbs"
            maxDimensions="74 in (L + W + H)"
            individualDimensions={{
              length: 41,
              width: 11,
              height: 22,
            }}
            isExpanded={expandedCards.oversized}
            onToggle={() => toggleCard('oversized')}
          />
        </div>
      </div>
    </div>
  )
}

export default WinterLuggageSizes
